import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../store';
import { authAPI } from '../../api';
import './AdminLayout.css';

// 관리자 메뉴 정의
const ADMIN_MENUS = [
  { id: 'dashboard', icon: '📊', label: '대시보드', group: '개요' },
  { id: 'general', icon: '⚙️', label: '일반 관리', group: '설정' },
  { id: 'login-mode', icon: '🔑', label: '로그인 모드', group: '설정' },
  { id: 'main-menu', icon: '📱', label: '메인 메뉴', group: '설정' },
  { id: 'subjects', icon: '📚', label: '종목 관리', group: '콘텐츠' },
  { id: 'questions', icon: '❓', label: '문제 데이터', group: '콘텐츠' },
  { id: 'type-stats', icon: '📈', label: '유형 통계', group: '콘텐츠' },  // ✅ 유형별 문제 수
  { id: 'image-editor', icon: '🖼️', label: '이미지 에디터', group: '콘텐츠' },
  { id: 'rest-box', icon: '🎨', label: '휴식 박스', group: '콘텐츠' },
  { id: 'notices', icon: '📢', label: '공지사항', group: '콘텐츠' },
  { id: 'users', icon: '👥', label: '회원 관리', group: '회원' },
  { id: 'suggestions', icon: '📨', label: '건의사항', group: '회원' },
  { id: 'study', icon: '💬', label: '함께공부', group: '회원' },
  { id: 'content-monitor', icon: '🗂️', label: '외부글 관리', group: '회원' },
  { id: 'category-manager', icon: '📂', label: '카테고리 관리', group: '회원' },  // ✅ 추가
  { id: 'account', icon: '🔐', label: '관리자 계정', group: '보안' },
];

interface Props {
  activeMenu: string;
  onMenuChange: (menu: string) => void;
  children: React.ReactNode;
}

const AdminLayout: React.FC<Props> = ({ activeMenu, onMenuChange, children }) => {
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();
  const [collapsed, setCollapsed] = useState(false);

  const handleLogout = async () => {
    try { await authAPI.logout(); } catch (e) {}
    logout();
    navigate('/login');
  };

  const groups = Array.from(new Set(ADMIN_MENUS.map(m => m.group)));

  return (
    <div className="admin-layout">
      {/* 사이드바 */}
      <aside className={`admin-sidebar ${collapsed ? 'collapsed' : ''}`}>
        {/* 사이드바 헤더 */}
        <div className="admin-sidebar-header">
          <button className="admin-logo" onClick={() => navigate('/')}>
            <div className="admin-logo-icon">M</div>
            {!collapsed && <span className="admin-logo-text">Mohabwe</span>}
          </button>
          <button className="collapse-btn" onClick={() => setCollapsed(!collapsed)}>
            {collapsed ? '→' : '←'}
          </button>
        </div>

        {/* 관리자 정보 */}
        {!collapsed && (
          <div className="admin-profile">
            <div className="admin-avatar">
              {user?.username?.charAt(0).toUpperCase()}
            </div>
            <div>
              <div className="admin-name">{user?.username}</div>
              <div className="admin-role">👑 관리자</div>
            </div>
          </div>
        )}

        {/* 메뉴 */}
        <nav className="admin-nav">
          {groups.map(group => (
            <div key={group} className="admin-nav-group">
              {!collapsed && (
                <div className="admin-nav-group-label">{group}</div>
              )}
              {ADMIN_MENUS.filter(m => m.group === group).map(menu => (
                <button
                  key={menu.id}
                  className={`admin-nav-item ${activeMenu === menu.id ? 'active' : ''}`}
                  onClick={() => onMenuChange(menu.id)}
                  title={collapsed ? menu.label : ''}
                >
                  <span className="admin-nav-icon">{menu.icon}</span>
                  {!collapsed && <span className="admin-nav-label">{menu.label}</span>}
                </button>
              ))}
            </div>
          ))}
        </nav>

        {/* collapsed 상태일 때만 사이드바 하단에 아이콘 표시 */}
        {collapsed && (
          <div className="admin-sidebar-footer">
            <button className="admin-nav-item" onClick={() => navigate('/')} title="메인으로">
              <span className="admin-nav-icon">🏠</span>
            </button>
            <button className="admin-nav-item logout" onClick={handleLogout} title="로그아웃">
              <span className="admin-nav-icon">🚪</span>
            </button>
          </div>
        )}
      </aside>

      {/* 메인 래퍼 */}
      <div className="admin-main-wrapper">
        {/* 상단 topbar - 메인으로/로그아웃 항상 우측 상단 고정 */}
        <div className="admin-topbar">
          <div className="admin-topbar-left">
            <span className="admin-topbar-title">⚙️ 관리자 페이지</span>
          </div>
          <div className="admin-topbar-right">
            <button className="btn btn-ghost btn-sm" onClick={() => navigate('/')}>
              🏠 메인으로
            </button>
            <button className="btn btn-sm admin-logout-btn" onClick={handleLogout}>
              🚪 로그아웃
            </button>
          </div>
        </div>

        {/* 메인 컨텐츠 */}
        <main className="admin-main">
          {children}
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
export { ADMIN_MENUS };
